import React, { useMemo } from "react";
// Libraries
import { useQuery } from "@apollo/client";
import { Column } from "react-table";
// Utils
import { getBUFromLocalStorage } from "helpers/storage";
// API
import { GET_ADDRESS_BOOK_LIST } from "api/AddressBook/queries";
import { GetContactList, GetContactList_contacts, GetContactListVariables } from "api/AddressBook/types/GetContactList";
import { EmptyStatePage } from "api/graphql-global-types";
// Components
import { LoadingOverlay, Flex, Spacer, Table, NoItemsFound, EmptyState, TablePaginationTarget } from "components";
import { TableActionColumn } from "components/Settings/AddressBook/AddressListTable/TableActionColumn";
import { getActionsColumn, TABLE_ROW_ACTIONS_COLUMN_ID } from "components/Common/Table";
// Styles
import tablePaginationStyles from "components/Common/Table/TablePagination/TablePagination.module.scss";

const COLUMN_IDS = {
  CPH: "farmId",
  ORGANISATION_NAME: "organisationName",
  CONTACT_NAME: "name",
  ADDRESS: "address",
  EMAIL: "email",
  PHONE: "phoneNumber",
  CONTACT_TYPE: "contactType",
  ACTION_BUTTONS: TABLE_ROW_ACTIONS_COLUMN_ID,
};

export const formatAddressString = ({
  addressLine1,
  addressLine2,
  city,
  country,
  postcode,
}: GetContactList_contacts): string => {
  return (
    (addressLine1 ? `${addressLine1}, ` : "") +
    (addressLine2 ? `${addressLine2}, ` : "") +
    (city ? `${city}, ` : "") +
    (country && country.name ? `${country.name}, ` : "") +
    (postcode ? `${postcode}` : "")
  );
};

export const AddressListTable: React.FC = () => {
  const businessUnit = getBUFromLocalStorage();

  const { data, loading } = useQuery<GetContactList, GetContactListVariables>(GET_ADDRESS_BOOK_LIST, {
    variables: {
      businessUnit,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const formatContactTypeString = (contactType: string): string => {
    const contactTypeInLowerCase = contactType.toLowerCase();
    return contactTypeInLowerCase.charAt(0).toUpperCase() + contactTypeInLowerCase.slice(1);
  };

  const tableId = "addressBookContactsTable";
  const contacts = (data?.contacts || []) as GetContactList_contacts[];
  const isEmptyStateVisible = !contacts.length;
  const memoizedTable = useMemo(() => contacts, [contacts]);
  //@ts-expect-error
  const columns: Column<GetContactList_contacts>[] = useMemo(
    () => [
      {
        id: COLUMN_IDS.CPH,
        Header: "CPH",
        accessor: "farmId",
        Cell: ({ row }): string => row.values[COLUMN_IDS.CPH] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.ORGANISATION_NAME,
        Header: "Organisation or Holding Name",
        accessor: "organisationName",
        Cell: ({ row }): string => row.values[COLUMN_IDS.ORGANISATION_NAME] || "\u2014",
        minWidth: 250,
        width: 250,
      },
      {
        id: COLUMN_IDS.CONTACT_NAME,
        Header: "Contact Name",
        accessor: "name",
        Cell: ({ row }): string => row.values[COLUMN_IDS.CONTACT_NAME] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.ADDRESS,
        Header: "Address",
        accessor: (data): string | null => formatAddressString(data) || null,
        Cell: ({ row }): string => row.values[COLUMN_IDS.ADDRESS] || "\u2014",
        minWidth: 250,
        width: 250,
      },
      {
        id: COLUMN_IDS.EMAIL,
        Header: "Email",
        accessor: "email",
        Cell: ({ row }): string => row.values[COLUMN_IDS.EMAIL] || "\u2014",
        minWidth: 250,
        width: 250,
      },
      {
        id: COLUMN_IDS.PHONE,
        Header: "Telephone",
        accessor: "phoneNumber",
        Cell: ({ row }): string => row.values[COLUMN_IDS.PHONE] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        id: COLUMN_IDS.CONTACT_TYPE,
        Header: "Contact Type",
        accessor: ({ contactType }): string | null => (contactType ? formatContactTypeString(contactType) : null),
        Cell: ({ row }): string => row.values[COLUMN_IDS.CONTACT_TYPE] || "\u2014",
        minWidth: 150,
        width: 150,
      },
      {
        ...getActionsColumn<GetContactList_contacts>(),
        // eslint-disable-next-line react/display-name
        Cell: ({ row: { original } }): JSX.Element => (
          <>
            <TableActionColumn addressId={original.id} />
          </>
        ),
        sticky: "right",
      },
    ],
    [tableId],
  );

  if (loading) return <LoadingOverlay />;

  if (isEmptyStateVisible) {
    return <EmptyState page={EmptyStatePage.ADDRESS_BOOK} />;
  }

  if (!contacts.length) {
    return <NoItemsFound title="No contacts added" />;
  }

  return (
    <>
      <Flex container>
        <Flex
          item
          itemGutter
          xs="fill"
          className={tablePaginationStyles.table_pagination_target}
          container
          containerJustifyContent="flex-end"
        >
          <TablePaginationTarget tableId={tableId} />
        </Flex>

        <Spacer baselineHeight={1} />

        <Flex item itemGutter xs="fill">
          <Table<GetContactList_contacts> columns={columns} data={memoizedTable} pagination tableId={tableId} />
          <Spacer baselineHeight={2} />
        </Flex>
      </Flex>
    </>
  );
};
